import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import api from '../config/axios';
import { showSuccess, showError, showLoading, closeAlert } from '../utils/swal';
import { getEffectiveRole } from '../utils/roles';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};


export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // Configure api defaults
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
    }, []);

    // Check if user is logged in on app start
    useEffect(() => {
        const checkAuth = async () => {
            const token = localStorage.getItem('token');
            const cachedUser = localStorage.getItem('user');

            if (!token) {
                setUser(null);
                setLoading(false);
                return;
            }

            if (cachedUser) {
                try {
                    const parsed = JSON.parse(cachedUser);
                    const effectiveRole = getEffectiveRole(parsed);
                    if (effectiveRole) parsed.role = effectiveRole;
                    if (effectiveRole === 'super_admin') parsed.permissions = 'all';
                    setUser(parsed);
                    // Only set loading to false if permissions are loaded/valid
                    if (parsed.permissions === 'all' || (Array.isArray(parsed.permissions) && parsed.permissions.length > 0)) {
                        setLoading(false);
                    }
                } catch (_) {}
            }

            try {
                const response = await api.get('/auth/me', { timeout: 8000 });
                const userData = response.data.user;

                const effectiveRole = getEffectiveRole(userData);
                if (effectiveRole) userData.role = effectiveRole;

                if (effectiveRole === 'super_admin') {
                    userData.permissions = 'all';
                } else if (!userData.permissions) {
                    userData.permissions = [];
                }
                
                localStorage.setItem('user', JSON.stringify(userData));
                setUser(userData);
            } catch (error) {
                console.error('Auth check failed:', error);
                const isTimeoutOrNetwork = error.code === 'ECONNABORTED' || error.message?.includes('timeout') || !error.response;
                if (!isTimeoutOrNetwork || !cachedUser) {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    delete api.defaults.headers.common['Authorization'];
                    setUser(null);
                }
            } finally {
                setLoading(false);
            }
        };

        checkAuth();
    }, []);

    const login = async (email, password) => {
        try {
            const response = await api.post('/auth/login', { email, password });
            const { token, user } = response.data;

            if (!token || !user) {
                return { success: false, error: 'Invalid response from server' };
            }

            localStorage.setItem('token', token);
            const loginRole = getEffectiveRole(user);
            if (loginRole) user.role = loginRole;
            if (loginRole === 'super_admin') {
                user.permissions = 'all';
            } else if (!user.permissions) {
                user.permissions = [];
            }
            localStorage.setItem('user', JSON.stringify(user));
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            setUser(user);

            return { success: true, user };
        } catch (error) {
            const approvalStatus = error.response?.data?.approvalStatus;
            let title = 'Login Failed';
            let message = 'An error occurred during login';

            if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
                title = 'Connection Error';
                message = 'Unable to connect to server. Please check your internet connection and ensure the server is running.';
            } else if (error.response?.status === 401) {
                title = 'Invalid Credentials';
                message = error.response?.data?.message || 'Email or password is incorrect';
            } else if (error.response?.status === 403 && approvalStatus === 'pending') {
                title = 'Pending Approval';
                message = error.response?.data?.message || 'Your account is pending approval.';
            } else if (error.response?.status === 403 && approvalStatus === 'rejected') {
                title = 'Account Rejected';
                message = error.response?.data?.message || 'Your account registration has been rejected.';
            } else if (error.response?.status === 403 && error.response?.data?.isFirstLogin) {
                title = 'Set your password';
                message = error.response?.data?.message || 'Use the link in your invitation email to set your password before signing in.';
            } else if (error.response?.status === 400) {
                title = 'Validation Error';
                message = error.response?.data?.message || 'Please check your input';
            } else if (error.response?.data?.message) {
                message = error.response.data.message;
            } else if (error.message) {
                message = error.message;
            }

            return {
                success: false,
                error: message,
                title,
                approvalStatus,
                isFirstLogin: !!error.response?.data?.isFirstLogin
            };
        }
    };

    const logout = async () => {
        try {
            await api.post('/auth/logout');
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            delete api.defaults.headers.common['Authorization'];
            setUser(null);
            showSuccess('Logged Out', 'You have been logged out successfully');
        }
    };

    const changePassword = async (currentPassword, newPassword) => {
        try {
            await api.post('/auth/change-password', {
                currentPassword,
                newPassword
            });
            showSuccess('Password Changed', 'Your password has been updated successfully!');
            return { success: true };
        } catch (error) {
            const message = error.response?.data?.message || 'Password change failed';
            showError('Password Change Failed', message);
            return { success: false, error: message };
        }
    };

    const refreshToken = async () => {
        try {
            const response = await api.post('/auth/refresh');
            const { token } = response.data;
            localStorage.setItem('token', token);
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            return { success: true };
        } catch (error) {
            console.error('Token refresh failed:', error);
            logout();
            return { success: false };
        }
    };

    const refreshUser = async () => {
        try {
            const response = await api.get('/auth/me');
            const userData = response.data.user;

            const refreshedRole = getEffectiveRole(userData);
            if (refreshedRole) userData.role = refreshedRole;

            if (refreshedRole === 'super_admin') {
                userData.permissions = 'all';
            } else if (!userData.permissions) {
                userData.permissions = [];
            }
            
            // Update localStorage to keep it in sync
            localStorage.setItem('user', JSON.stringify(userData));

            setUser(userData);
            return { success: true, user: userData };
        } catch (error) {
            console.error('Failed to refresh user:', error);
            return { success: false };
        }
    };

    const value = useMemo(() => {
        const effectiveRole = getEffectiveRole(user);
        const isSuperAdmin = effectiveRole === 'super_admin';
        const isAdmin = effectiveRole === 'admin';
        const isTeamManager = effectiveRole === 'team_manager';
        const isSalesExecutive = effectiveRole === 'sales_executive';

        const isSalesManager = effectiveRole === 'team_manager';
        const isDepartmentManager = effectiveRole === 'department_manager';
        const isAgent = effectiveRole === 'sales_executive';

        return {
            user,
            loading,
            login,
            logout,
            changePassword,
            refreshToken,
            refreshUser,
            isAuthenticated: !!user,
            isSuperAdmin,
            isAdmin,
            isTeamManager,
            isSalesExecutive,
            // Legacy support
            isSalesManager,
            isDepartmentManager,
            isAgent
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, loading]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
